import { createContext, useContext, useRef, useState, useCallback, type ReactNode } from 'react'
import type { Workspace, EventLevel, AnnotationSeverity } from '../types'

export interface EventsWorkspaceState {
  filter?: string
  timeRange?: string
  startTime?: string
  endTime?: string
  search?: string
  application?: string
  levels: EventLevel[]
  viewMode: 'list' | 'grouped'
  hasExceptionFilter: boolean
  hasAnnotationsFilter: boolean
  selectedAnnotationSeverities: AnnotationSeverity[]
}

interface WorkspaceHeaderContextType {
  /** Whether Events page is mounted and has registered its handlers */
  isActive: boolean
  /** Get the current Events state (ref-based, no re-renders) */
  getState: () => EventsWorkspaceState | null
  /** Get the restore callback (ref-based, no re-renders) */
  getOnRestore: () => ((ws: Workspace) => void) | null
  /** Called by Events page on mount */
  register: (
    stateRef: React.RefObject<EventsWorkspaceState>,
    onRestore: (ws: Workspace) => void,
  ) => void
  /** Called by Events page on unmount */
  unregister: () => void
}

const WorkspaceHeaderContext = createContext<WorkspaceHeaderContextType>({
  isActive: false,
  getState: () => null,
  getOnRestore: () => null,
  register: () => {},
  unregister: () => {},
})

export function WorkspaceHeaderProvider({ children }: { children: ReactNode }) {
  const [isActive, setIsActive] = useState(false)
  const stateRef = useRef<React.RefObject<EventsWorkspaceState> | null>(null)
  const restoreRef = useRef<((ws: Workspace) => void) | null>(null)

  const getState = useCallback(() => stateRef.current?.current ?? null, [])
  const getOnRestore = useCallback(() => restoreRef.current, [])

  const register = useCallback(
    (ref: React.RefObject<EventsWorkspaceState>, onRestore: (ws: Workspace) => void) => {
      stateRef.current = ref
      restoreRef.current = onRestore
      setIsActive(true)
    },
    [],
  )

  const unregister = useCallback(() => {
    stateRef.current = null
    restoreRef.current = null
    setIsActive(false)
  }, [])

  return (
    <WorkspaceHeaderContext.Provider value={{ isActive, getState, getOnRestore, register, unregister }}>
      {children}
    </WorkspaceHeaderContext.Provider>
  )
}

export function useWorkspaceHeader() {
  return useContext(WorkspaceHeaderContext)
}
